/* @flow */
import cx from 'classnames';
import * as React from 'react';
import ReactDOM from 'react-dom';
import invariant from 'invariant';

import Button from 'components/Button';
import EventTracking from 'lib/EventTracking';
import Glyph from 'components/Glyph';
import LeadActions from 'actions/LeadActions';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import NameAndSignatureQuery from 'components/NameAndSignatureQuery';
import PotentialChatMember from 'entities/PotentialChatMember';
import StatusLabel from 'components/StatusLabel';
import Text from 'components/Text';
import TextEntities from 'lib/TextEntities';
import TextWithEntities from 'components/TextWithEntities';
import ToastActions from 'actions/ToastActions';
import UserAvatar from 'users/components/UserAvatar';

import './styles/ChatListSearchItem.scss';

const screenName = 'invite_teacher_leads_chat_search_item';

type Props = {
  member: PotentialChatMember,
  query: string,
  onSelect: (member: PotentialChatMember) => void,
  highlight: boolean,
  selected: boolean,
  added?: ?boolean,
};

class ChatListSearchItem extends React.Component<Props, *> {
  static defaultProps = {
    highlight: false,
    selected: false,
  };
  componentDidMount() {
    const { member } = this.props;
    if (member.isTeacherLead()) {
      if (member.isInvited()) {
        EventTracking.view(screenName, {
          state: 'invited',
        });
      } else if (member.isInvitable()) {
        EventTracking.view(screenName, {
          state: 'invitable',
        });
      }
    }
  }

  // Avoid redrawing like crazy when the highlight
  // props is changed when hovered on element.
  shouldComponentUpdate(nextProps: Props) {
    return (
      nextProps.highlight !== this.props.highlight ||
      nextProps.query !== this.props.query ||
      nextProps.onSelect !== this.props.onSelect ||
      nextProps.selected !== this.props.selected ||
      nextProps.added !== this.props.added ||
      // $FlowFixMe cant find .state , assume it might be added elsewhere?
      !!nextProps.member.changed.state
    );
  }

  click = () => {
    const link = ReactDOM.findDOMNode(this.refs.link);
    invariant(link instanceof window.HTMLElement, 'ChatListSearchItem findDOMNode failed');
    link.click();
  };
  handleClick = (event: any) => {
    event.preventDefault();
    this.props.onSelect(this.props.member);
  };
  inviteMember = () => {
    const onSuccess = () => {
      ToastActions.showSuccessToast(
        `Thank you! ${this.props.member.getName()} has been sent an invitation.`
      );
    };

    LeadActions.sendInvitations([this.props.member.id], 'chat').then(
      onSuccess,
      ToastActions.showDefaultErrorToast
    );
  };
  render() {
    const { member } = this.props;
    const fullName = member.getRole() === 'teacher' ? member.getUser().getFullName() : '';

    let subText = member.getConnectionDescription();
    let privateNote = member.getNote();

    if (this.props.query !== '') {
      const parser = TextEntities.createTextMatchingParser(this.props.query);
      subText = <TextWithEntities text={subText} entities={parser(subText)} />;

      if (privateNote) {
        const privateNoteMatches = parser(privateNote);
        privateNote = <TextWithEntities text={privateNote} entities={privateNoteMatches} />;
      }
    }

    if (privateNote) {
      privateNote = (
        <Text className="ChatListSearchItem-privateNote" truncate>
          {privateNote}
        </Text>
      );
    }

    const user = member.getUser();

    let item = (
      <div>
        <MediaObject gutter="xxsmall" center>
          <UserAvatar className="ChatListSearchItem-glyph" size="medium" user={user.toJSON()} />
          <div className="ChatListSearchItem-bodyText">
            <Text className="ChatListSearchItem-name" truncate>
              <NameAndSignatureQuery
                displayName={member.getName()}
                fullName={fullName}
                query={this.props.query}
              />
            </Text>
            {privateNote}
            <Text className="ChatListSearchItem-subText" truncate>
              {subText}
            </Text>
          </div>
        </MediaObject>
      </div>
    );

    let state;
    if (this.props.added) {
      state = 'Added';
    } else if (member.isPending()) {
      state = 'Pending';
    } else if (member.isUnder13()) {
      state = 'Under 13';
    }

    if (member.isTeacherLead()) {
      if (member.isInvited()) {
        item = (
          <MediaObject figure="right" center>
            {item}
            <StatusLabel className="ChatListSearchItem-status">Invited</StatusLabel>
          </MediaObject>
        );
      } else if (member.isInvitable()) {
        item = (
          <MediaObject figure="right" center>
            {item}
            <Button
              className="ChatListSearchItem-inviteButton"
              size="xsmall"
              level="secondary"
              onClick={this.inviteMember}
              eventProperties={{
                teacher_lead_id: member.id,
              }}
              eventElementName="ChatListSearchItem-inviteButton">
              Invite
            </Button>
          </MediaObject>
        );
      } else {
        return null; // if a teacher lead is neither invited nor invitable, don't display them
      }
    }

    if (this.props.selected) {
      item = (
        <MediaObject figure="right">
          {item}
          <Glyph
            className="ChatListSearchItem-selectedGlyph"
            name="check"
            size="medium"
            color="brand"
          />
        </MediaObject>
      );
    } else if (state) {
      item = (
        <MediaObject figure="right">
          {item}
          <StatusLabel className="ChatListSearchItem-status">{state}</StatusLabel>
        </MediaObject>
      );
    }

    return (
      <li
        className={cx({
          ChatListSearchItem: true,
          'ChatListSearchItem-highlight': this.props.highlight,
          'ChatListSearchItem-under13': member.isUnder13(),
          'ChatListSearchItem-unavailable': member.isUnavailable(),
          'ChatListSearchItem-selected': this.props.selected,
          'ChatListSearchItem-added': this.props.added,
          'ChatListSearchItem-invite': member.isInvitable() || member.isInvited(),
        })}>
        <Link
          className="ChatListSearchItem-link"
          ref="link"
          color="pitch"
          onClick={this.handleClick}
          eventElementName="ChatListSearchItem-link">
          <div
            className="ChatListSearchItem-linkContent"
            data-test-selector={`chat-list-search-item-${member.getName()}`}>
            {item}
          </div>
        </Link>
      </li>
    );
  }
}

export default ChatListSearchItem;
